import React, { useState, useRef, useEffect, useMemo, useContext, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useNavigation } from '@/contexts/NavigationContext';
import { Settings, Crown, Loader2, Clock, LogOut, ChevronDown, HelpCircle, Search, Bot, ChevronsRight, Menu } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSidebar } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/components/AuthProvider';
import { PlanCreditsBar } from './PlanCreditsBar';
import { ACCOUNT_BILLING_URL, ACCOUNT_PLANS_URL, SidebarAccountFooter } from './SidebarAccountFooter';
import { BusinessAccountContext } from '@/contexts/BusinessAccountContext';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';
import { dashboardApi } from '@/lib/api';
import { useCurrentSubscription, pendingSubscriptionRefetchInterval } from '@/hooks/useCurrentSubscription';
import { useAiChat } from '@/contexts/GenUIContext';
import type { Subscription } from '@/lib/api/services/subscriptions';
import { isTrialActuallyExpired, isSubscriptionActive } from '@/lib/subscription-utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAvailableSearchItems, openSearchResult, searchAndRankItems } from '../search/GlobarSearch';
import { AI_CREDITS_STATS_QUERY_KEY } from '@/lib/api/aiCreditsGuard';
import {
  NEXA_FAB_CLEARANCE_SELECTOR,
  shouldRaiseNexaFab,
} from '@/lib/nexaFabClearance';
import {
  DOCKED_CHAT_CHANGED_EVENT,
  isDockedChatOpen,
  type DockedChatChangedDetail,
} from '@/lib/inbox/dockedChat';
const notification_icon = '/assets/notification-icon.svg';
const dropdown_icon = '/assets/dropdown-close-icon.svg';
const we_next_logo = '/assets/wenext_logo_dark.png';
const small_we_next_logo = '/assets/loading-icons/we-next-icon.jpg';
const nexa_ai_icon = '/onboardingimg/wenext-icon.svg';

const PremiumButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [ripples, setRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rippleIdRef = useRef(0);

  const createRipple = (x?: number, y?: number) => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const rippleX = x !== undefined ? x : Math.random() * rect.width;
    const rippleY = y !== undefined ? y : Math.random() * rect.height;

    const newRipple = {
      id: rippleIdRef.current++,
      x: rippleX,
      y: rippleY,
    };

    setRipples((prev) => [...prev, newRipple]);

    setTimeout(() => {
      setRipples((prev) => prev.filter((ripple) => ripple.id !== newRipple.id));
    }, 800);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      createRipple();
    }, 1500); // Create a new ripple every 1.5 seconds

    return () => clearInterval(interval);
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    createRipple(x, y);
    onClick();
  };

  return (
    <button
      ref={buttonRef}
      onClick={handleClick}
      className="relative overflow-hidden px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-white font-semibold text-xs sm:text-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95 group"
      style={{
        background: 'linear-gradient(135deg, #f59e0b 0%, #eab308 50%, #f59e0b 100%)',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute rounded-full bg-white/30 pointer-events-none"
          style={{
            left: `${ripple.x}px`,
            top: `${ripple.y}px`,
            width: '0px',
            height: '0px',
            transform: 'translate(-50%, -50%)',
            animation: 'ripple 800ms ease-out',
          }}
        />
      ))}

      <span className="relative flex items-center gap-1.5 sm:gap-2 z-10">
        <Crown className="h-3 w-3 sm:h-4 sm:w-4" />
        <span className="whitespace-nowrap">Plans</span>
      </span>

      <style>{`
        @keyframes ripple {
          to {
            width: 400px;
            height: 400px;
            opacity: 0;
          }
        }
      `}</style>
    </button>
  );
};

interface TopNavigationProps {
  onStartTour?: () => void;
  onOpenBilling?: () => void;
  variant?: 'classic' | 'card';
  title?: string;
}

export const TopNavigation: React.FC<TopNavigationProps> = ({
  onStartTour,
  variant = 'classic',
  title,
}) => {
  const isCard = variant === 'card';
  const { user, signOut } = useAuth();

  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isAgency = !!(user as any)?.is_agency;

  const [searchExpanded, setSearchExpanded] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { toggleChat, isOpen: isNexaOpen } = useAiChat();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isInboxConversationOpen =
    location.pathname.startsWith('/home/channels/') &&
    Boolean(searchParams.get('conversationId'));
  const [dockedChatOpen, setDockedChatOpen] = useState(() => isDockedChatOpen());
  useEffect(() => {
    const onDockedChatChanged = (event: Event) => {
      const detail = (event as CustomEvent<DockedChatChangedDetail>).detail;
      setDockedChatOpen(!!detail?.open);
    };
    window.addEventListener(DOCKED_CHAT_CHANGED_EVENT, onDockedChatChanged);
    return () => window.removeEventListener(DOCKED_CHAT_CHANGED_EVENT, onDockedChatChanged);
  }, []);
  const [raiseNexaFab, setRaiseNexaFab] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let raf = 0;
    const evaluate = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const isMobile = window.matchMedia('(max-width: 639px)').matches;
        const next = shouldRaiseNexaFab({
          isMobile,
          clearanceElements: document.querySelectorAll(NEXA_FAB_CLEARANCE_SELECTOR),
          viewportHeight: window.innerHeight,
        });
        setRaiseNexaFab((prev) => (prev === next ? prev : next));
      });
    };

    evaluate();
    const scrollRoot = document.getElementById('app-main-scroll');
    scrollRoot?.addEventListener('scroll', evaluate, { passive: true });
    window.addEventListener('resize', evaluate);
    const observeRoot = scrollRoot ?? document.body;
    const mo = new MutationObserver(evaluate);
    mo.observe(observeRoot, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      scrollRoot?.removeEventListener('scroll', evaluate);
      window.removeEventListener('resize', evaluate);
      mo.disconnect();
    };
  }, [location.pathname]);

  const handleGenUIClick = () => {
    toggleChat();
  };


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSearchQuery('');
        setSearchExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const availableSearchItems = useAvailableSearchItems();

  const searchResults = useMemo(() => {
    const navigable = availableSearchItems.filter((item) => !item.isComingSoon);
    if (!searchQuery) return navigable.slice(0, 20);
    return searchAndRankItems(navigable, searchQuery).slice(0, 20);
  }, [searchQuery, availableSearchItems]);


  useEffect(() => {
    if (isSuperAdmin || isAgency) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isK = e.key === 'k' || e.key === 'K';
      const isModifier = e.ctrlKey || e.metaKey;

      if (isK && isModifier) {
        e.preventDefault();
        setSearchExpanded(true);
        setShowSuggestions(true);
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 50);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSuperAdmin, isAgency]);
  const navigate = useNavigate();
  const { interceptedNavigate } = useNavigation();

  const runPageSearch = useCallback(() => {
    if (!searchQuery.trim()) return;
    const top = searchResults[0];
    if (!top) return;
    setShowSuggestions(false);
    setSearchExpanded(false);
    setSearchQuery('');
    setMobileSearchOpen(false);
    openSearchResult(top, navigate);
  }, [searchQuery, searchResults, navigate]);
  const businessAccountContext = useContext(BusinessAccountContext);
  const selectedAccountId = businessAccountContext?.selectedAccountId || null;
  const { toggleSidebar, state } = useSidebar();
  const collapsed = state === 'collapsed';
  const isMobile = useIsMobile();
  const organizationId = user?.organization_id || (user as any)?.organizationId || null;

  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isAgent = roleRaw === 'AGENT';
  const isRegularAdmin = roleRaw === 'ADMIN' && !isSuperAdmin && !isAgency;
  const isUser = roleRaw === 'USER';

  const showBusinessSwitcher = isRegularAdmin || isUser || isAgent;

  const getUserInitials = useMemo(() => {
    if (user?.full_name) {
      return user.full_name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  }, [user]);

  const {
    data: currentSubscription,
    isLoading: subscriptionLoading,
  } = useCurrentSubscription({
    enabled: !!organizationId && isRegularAdmin,
    staleTime: 2 * 60 * 1000,
    // Polls while a payment waits for its webhook (shared with PlanCreditsBar).
    refetchInterval: pendingSubscriptionRefetchInterval,
  });

  const { data: headerStats, isLoading: aiCreditsLoading } = useQuery({
    queryKey: [...AI_CREDITS_STATS_QUERY_KEY, selectedAccountId, organizationId],
    enabled: (!!organizationId || !!selectedAccountId) && !isSuperAdmin && !isAgency,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const result = await dashboardApi.getAiCredits(selectedAccountId, organizationId);
      return result.data;
    },
  });

  const aiCreditsAvailable = Number(headerStats?.aiCredits?.available ?? 0);
  const aiCreditsDisplayValue = aiCreditsAvailable < 0.3 ? 0 : aiCreditsAvailable;

  const showPlansButton = isRegularAdmin;


  const handleSignOut = async () => {
    await signOut();
    if (isAgency) {
      interceptedNavigate('/agency/login');
    } else if (isSuperAdmin) {
      interceptedNavigate('/admin/login');
    } else {
      interceptedNavigate('/auth');
    }
  };

  const handleProfileSettings = () => {
    if (isSuperAdmin) {
      interceptedNavigate('/admin/settings');
    } else if (isAgency) {
      interceptedNavigate('/home/agency/settings');
    } else {
      interceptedNavigate('/home/account-settings');
    }
  };

  const handleSettings = () => {
    handleProfileSettings();
  };

  const handleTeamSettings = () => {
    interceptedNavigate('/home/team-management');
  };

  const handleLogoClick = () => {
    if (isSuperAdmin) {
      interceptedNavigate('/admin/dashboard');
    } else if (isAgency) {
      interceptedNavigate('/home/agency/dashboard');
    } else {
      interceptedNavigate('/home');
    }
  };

  const PlanStatus: React.FC<{
    subscriptionLoading: boolean;
    currentSubscription: Subscription | null | undefined;
    onClick: () => void;
  }> = ({ subscriptionLoading, currentSubscription, onClick }) => {
    const [ripples, setRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
    const planStatusRef = useRef<HTMLButtonElement | HTMLDivElement>(null); // Updated ref type
    const rippleIdRef = useRef(0);

    const planName = currentSubscription?.plan?.name || 'No Plan Selected';
    const status = currentSubscription?.status || 'none';
    const isPending = status === 'pending';
    const trialExpired = isTrialActuallyExpired(currentSubscription);
    const isTrialing = status === 'trialing' && !trialExpired;
    const isActive = status === 'active';
    const trialEndsAt = currentSubscription?.trial_end ? new Date(currentSubscription.trial_end) : null;
    const trialDaysRemaining =
      isTrialing && trialEndsAt
        ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : null;

    const createRipple = () => {
      if (!planStatusRef.current) return;

      const rect = planStatusRef.current.getBoundingClientRect();
      const rippleX = Math.random() * rect.width;
      const rippleY = Math.random() * rect.height;

      const newRipple = {
        id: rippleIdRef.current++,
        x: rippleX,
        y: rippleY,
      };

      setRipples((prev) => [...prev, newRipple]);

      setTimeout(() => {
        setRipples((prev) => prev.filter((ripple) => ripple.id !== newRipple.id));
      }, 800);
    };

    useEffect(() => {
      if (isActive || isTrialing || isPending || subscriptionLoading) {
        const interval = setInterval(() => {
          createRipple();
        }, 1500); // Create a new ripple every 1.5 seconds

        return () => clearInterval(interval);
      }
      return () => { }; // No interval if not showing standard status
    }, [isActive, isTrialing, isPending, subscriptionLoading]); // Re-run effect if status changes

    if (subscriptionLoading) {
      return (
        <div
          aria-busy="true"
          aria-label="Loading plan"
          className="wenext-shimmer h-7 sm:h-8 w-24 sm:w-28 rounded-md sm:rounded-lg"
        />
      );
    }

    if (!isActive && !isTrialing && !isPending && !subscriptionLoading) {
      return (
        <button
          ref={planStatusRef as any} // Cast needed because ref can be HTMLDivElement too
          onClick={onClick} // Use the onClick prop directly
          className="relative overflow-hidden px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-white font-semibold text-xs sm:text-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95 group flex items-center gap-2"
          style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #eab308 50%, #f59e0b 100%)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

          <Crown className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span className="whitespace-nowrap">Upgrade Plan</span>
        </button>
      );
    }

    const statusClasses = (() => {
      if (isPending) return 'bg-red-500 text-white';
      if (isTrialing) return 'bg-orange-500 text-white';
      if (isActive) return 'bg-green-500 text-white';
      return 'bg-gray-500 text-white'; // Fallback, though should be covered
    })();

    return (
      <div
        ref={planStatusRef as any}
        className={`flex items-center justify-between gap-2 sm:gap-3 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md sm:rounded-lg shadow-none relative overflow-hidden ${!subscriptionLoading ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
        onClick={!subscriptionLoading ? onClick : undefined}
      >
        {ripples.map((ripple) => (
          <span
            key={ripple.id}
            className="absolute rounded-full bg-white/20 pointer-events-none"
            style={{
              left: `${ripple.x}px`,
              top: `${ripple.y}px`,
              width: '0px',
              height: '0px',
              transform: 'translate(-50%, -50%)',
              animation: 'planRipple 800ms ease-out',
            }}
          />
        ))}

        <span className={`text-[12px] md:text-[14px] font-medium px-1.5 sm:px-2 py-0.5 rounded-full border-0 ${statusClasses} flex-shrink-0 whitespace-nowrap relative z-10`}>
          {planName}
        </span>
        {isPending && (
          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-amber-200 ml-1 relative z-10">
            <Loader2 className="h-2.5 w-2.5 sm:h-3 sm:w-3 animate-spin flex-shrink-0" />
          </div>
        )}
        {isTrialing && trialDaysRemaining !== null && (
          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-white/80 ml-1 relative z-10">
            <Clock className="h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0" />
            <span className="whitespace-nowrap hidden sm:inline">{trialDaysRemaining} day{trialDaysRemaining === 1 ? '' : 's'}</span>
          </div>
        )}

        <style>{`
            @keyframes planRipple {
              to {
                width: 300px;
                height: 300px;
                opacity: 0;
              }
            }
          `}</style>
      </div>
    );
  };

  const renderPlanStatus = () => {
    return <PlanStatus subscriptionLoading={subscriptionLoading} currentSubscription={currentSubscription} onClick={() => {
      interceptedNavigate(isSubscriptionActive(currentSubscription) ? ACCOUNT_BILLING_URL : ACCOUNT_PLANS_URL);
    }} />;
  };

  const AI_CREDITS_DATA = [
    { api: 'Template generation', costPerCall: 0.438 },
    { api: 'Campaign prediction', costPerCall: 0.254 },
    { api: 'Template insights', costPerCall: 0.264 },
    { api: 'Template body refine', costPerCall: 0.134 },
    { api: 'Analytics query', costPerCall: 0.896 },
    { api: 'Guide chat', costPerCall: 0.408 },
    { api: 'Meta ads analyze', costPerCall: 0.288 },
    { api: 'Livechat', costPerCall: 0.36 },
    { api: 'WB AI Agent', costPerCall: 0.60 },
    { api: 'Automation flow', costPerCall: 0.36 },
    { api: 'Analytics', costPerCall: 0.30 },
    { api: 'Chatbot', costPerCall: 0.48 },
    { api: 'Form builder', costPerCall: 0.24 },
    { api: 'Image generator', costPerCall: 0.65 },
  ];

  const renderAiCreditsChip = () => (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center cursor-pointer">
            {aiCreditsLoading ? (
              <div
                aria-busy="true"
                aria-label="Loading AI credits"
                className="wenext-shimmer h-7 sm:h-8 w-24 sm:w-28 rounded-md"
              />
            ) : (
              <Badge
                variant="secondary"
                className="text-xs sm:text-sm font-semibold bg-gradient-to-r from-[#132846] to-[#17345a] text-white border border-[#2a466a] px-2.5 py-1.5 shadow-sm"
              >
                <Bot className="mr-1.5 h-3.5 w-3.5 text-white" />
                AI Credits: {aiCreditsDisplayValue.toFixed(2)}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className="p-0 border border-[#2a466a] bg-[#0d1e33] text-white rounded-lg shadow-xl overflow-hidden w-72"
        >
          <div className="px-3 py-2 bg-[#132846] border-b border-[#2a466a]">
            <p className="text-xs font-semibold text-white/80">≈ Approx API calls with your credits</p>
            <p className="text-sm font-bold text-white">{aiCreditsDisplayValue.toFixed(2)} Credits available</p>
          </div>
          <div className="px-3 py-1.5 flex items-center justify-between border-b border-[#2a466a]/70">
            <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wide">AI Call</span>
            <div className="flex gap-4">
              <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wide">≈ Calls</span>
              <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wide w-16 text-right">Per AI call</span>
            </div>
          </div>
          <div className="divide-y divide-[#2a466a]/50">
            {AI_CREDITS_DATA.map((row) => (
              <div key={row.api} className="flex items-center justify-between px-3 py-1.5">
                <span className="text-xs text-white/70 flex-1">{row.api}</span>
                <div className="flex gap-4 flex-shrink-0">
                  <span className="text-xs font-semibold text-white tabular-nums">
                    ≈ {Math.floor(aiCreditsDisplayValue / row.costPerCall).toLocaleString()}
                  </span>
                  <span className="text-xs text-white/50 tabular-nums w-16 text-right">{row.costPerCall.toFixed(3)}</span>
                </div>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const hasSubscription = !subscriptionLoading && currentSubscription !== null && currentSubscription !== undefined;
  const showPlanStatus = subscriptionLoading || hasSubscription;

  const renderUserDropdown = () => {
    if (!isAgent && !isSuperAdmin && !isAgency && !isUser) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="user-menu-trigger"
            variant="ghost"
            className="group flex items-center gap-2 sm:gap-2.5 h-auto p-1.5 sm:p-2 md:p-2.5 rounded-lg border-1 border-transparent transition-all duration-300 hover:border-[#434343] hover:scale-[1.02] active:scale-[0.98] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            <Avatar className="h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9 ring-2 ring-gray-200 dark:ring-gray-700 ring-offset-1 transition-all duration-300 ">
              <AvatarImage src={user?.avatar_url} alt={user?.full_name || user?.email} />
              <AvatarFallback className="text-xs sm:text-sm font-semibold bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 transition-all duration-300">
                {getUserInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-start min-w-0 flex-1 max-w-[80px] sm:max-w-[100px] md:max-w-[140px] lg:max-w-[160px]">
              <span className="text-xs sm:text-sm font-medium truncate w-full text-left transition-colors duration-300 group-hover:text-[#434343]">
                {user?.full_name || (isSuperAdmin ? 'Admin' : isAgency ? 'Agency User' : 'Agent')}
              </span>
              <span className="text-[10px] sm:text-xs text-muted-foreground truncate w-full text-left hidden sm:block transition-colors duration-300 group-hover:text-[#434343]">
                {isSuperAdmin ? user?.email : isAgency ? 'Agency Account' : user?.role || 'Agent'}
              </span>
            </div>
            <ChevronDown className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-muted-foreground flex-shrink-0 transition-all duration-300 group-hover:text-[#434343] group-data-[state=open]:rotate-180" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56 sm:w-64 md:w-72 rounded-lg shadow-lg border bg-background/95 backdrop-blur-sm"
        >
          <DropdownMenuLabel className="px-3 py-3 sm:px-4 sm:py-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <Avatar className="h-10 w-10 sm:h-12 sm:w-12 ring-2 ring-primary/20">
                <AvatarImage src={user?.avatar_url} alt={user?.full_name || user?.email} />
                <AvatarFallback className="text-sm font-semibold bg-gradient-to-br from-primary/20 to-primary/10">
                  {getUserInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col space-y-0.5 min-w-0 flex-1">
                <p className="text-sm sm:text-base font-semibold leading-none truncate">
                  {user?.full_name || (isSuperAdmin ? 'Admin' : isAgency ? 'Agency User' : 'Agent')}
                </p>
                <p className="text-xs sm:text-sm leading-none text-muted-foreground truncate">
                  {user?.email}
                </p>
                <p className="text-xs text-muted-foreground/80 mt-1 truncate">
                  {isSuperAdmin ? 'Super Admin' : isAgency ? 'Agency Account' : user?.role || 'Agent'}
                </p>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleProfileSettings}
            className="cursor-pointer focus:bg-accent transition-colors duration-150"
          >
            <Settings className="mr-2 h-4 w-4" />
            <span className="text-sm sm:text-base">Settings</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="user-menu-logout"
            onClick={handleSignOut}
            className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/20 transition-colors duration-150"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span className="text-sm sm:text-base font-medium">Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <>
      <header className={cn(
        'bg-background flex items-center justify-start w-full relative',
        isCard
          ? 'shrink-0 border-b border-[#E4E7EC] px-3 py-3 gap-2 lg:gap-3 lg:px-[25px] lg:py-4'
          : 'h-14 sm:h-16 md:h-[62px] px-2 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-[10px] gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 border-b border-slate-200'
      )}
        style={{
          zIndex: 40,
        }}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleSidebar();
          }}
          className="h-7 w-7 sm:hidden flex-shrink-0 relative z-20"
          data-tour="sidebar-trigger"
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5 text-foreground" />
        </Button>

        <img
          src={small_we_next_logo}
          alt="WeNext"
          className="sm:hidden h-7 w-7 flex-shrink-0 rounded-[6px] object-contain"
        />

        {isCard && title && (
          <div className="hidden min-w-[200px] flex-1 gap-2 sm:flex sm:items-center">
            {collapsed && !isMobile && (
              <button
                type="button"
                onClick={toggleSidebar}
                aria-label="Expand sidebar"
                aria-expanded={false}
                title="Expand sidebar"
                className="-ml-1.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-muted hover:text-text-strongest"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            )}
            <h1 className="truncate text-[18px] font-semibold leading-tight tracking-[-0.01em] text-[#101828] lg:text-[22px]">
              {title}
            </h1>
          </div>
        )}

        {!isSuperAdmin && !isAgency && (
          <Button
            variant="outline"
            onClick={() => {
              setMobileSearchOpen(true);
              setTimeout(() => mobileSearchInputRef.current?.focus(), 50);
            }}
            className="sm:hidden flex items-center gap-2 h-9 px-3 flex-1 min-w-0 justify-start rounded-md border border-slate-300 bg-white text-muted-foreground hover:bg-slate-50"
            aria-label="Search"
          >
            <Search className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm truncate">Search...</span>
          </Button>
        )}

        {collapsed && !isMobile && !isCard && (
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Expand sidebar"
            title="Expand sidebar"
            className={cn(
              'hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-md border border-slate-300 bg-white text-[#6a7282] hover:bg-slate-50 hover:text-[#101828] transition-colors flex-shrink-0',
              !isCard && 'absolute top-1/2 -translate-y-1/2 z-20'
            )}
            style={{ left: isCard ? undefined : '15px', boxShadow: '0 1px 2px rgba(16,24,40,0.04)' }}
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        )}

        <div
          className={cn(
            'hidden sm:flex items-center z-10 transition-[left] duration-200 ease-smooth',
            isCard ? 'ml-auto min-w-0' : 'h-full absolute top-0 bottom-0'
          )}
          style={{
            left: isCard ? undefined : (collapsed && !isMobile ? '55px' : '15px'), // Snug beside the expand button when collapsed
            paddingLeft: '0px'
          }}
        >
          {!isSuperAdmin && !isAgency && (
            <div
              className="flex items-center gap-1.5 sm:gap-2 md:gap-3 flex-shrink-0 min-w-0 relative"
              ref={searchContainerRef}
            >
              <div className="hidden sm:flex items-center ml-2 relative">
                <div
                  className={cn(
                    "flex items-center rounded-md overflow-hidden border border-slate-300 bg-white transition-colors hover:bg-slate-50 cursor-text",
                    isCard
                      ? "h-9 w-[260px] min-w-[160px] max-w-full lg:w-[300px]"
                      : "h-10 w-[200px] md:w-[240px] lg:w-[300px] xl:w-[360px]"
                  )}
                  onClick={() => {
                    if (!searchExpanded) {
                      setSearchExpanded(true);
                    }
                    setShowSuggestions(true);
                    setTimeout(() => searchInputRef.current?.focus(), 100);
                  }}
                >
                  <div className={cn(
                    'flex items-center justify-center flex-shrink-0 text-muted-foreground',
                    isCard ? 'h-9 w-9' : 'h-10 w-10'
                  )}>
                    <Search className="h-4 w-4" />
                  </div>
                  <input
                    ref={searchInputRef}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        runPageSearch();
                      }
                      if (e.key === 'Escape') {
                        setShowSuggestions(false);
                        setSearchQuery('');
                        searchInputRef.current?.blur();
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                      }, 200);
                    }}
                    className="bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground/50 text-foreground transition-all duration-300 w-full pr-2 opacity-100 min-w-0"
                    placeholder="Search..."
                  />

                  <div className="flex items-center gap-1 pr-3 pointer-events-none select-none shrink-0">
                    <kbd className="hidden lg:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                      <span className="text-xs">
                        {typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl'}
                      </span>
                      K
                    </kbd>
                  </div>

                </div>

                {showSuggestions && (
                  <div className="absolute top-full left-0 mt-2 w-full z-50">
                    <div className="bg-background/95 backdrop-blur-xl border border-border/50 shadow-2xl rounded-xl animate-in fade-in slide-in-from-top-2 duration-200 overflow-hidden">
                      <div className="max-h-[290px] overflow-y-auto scrollbar-thin p-2">
                        {searchResults.length > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            {searchResults.map((item) => (
                              <div
                                key={item.id}
                                onClick={() => {
                                  openSearchResult(item, navigate);
                                  setShowSuggestions(false);
                                  setSearchQuery('');
                                  setSearchExpanded(false);
                                }}
                                className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-muted/60 cursor-pointer group transition-colors duration-150"
                              >
                                <div
                                  className="flex items-center justify-center h-7 w-7 rounded-md text-white transition-colors shrink-0"
                                  style={{ background: 'var(--secondary-color)' }}
                                >
                                  <item.icon className="h-3.5 w-3.5" />
                                </div>
                                <span className="text-sm font-medium text-foreground truncate">{item.title}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="py-6 text-center">
                            <p className="text-sm text-muted-foreground">No matches found</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {isRegularAdmin && onStartTour && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onStartTour}
                  className="h-7 w-7 hidden sm:h-8 sm:w-8 md:h-9 md:w-9 lg:h-10 lg:w-10 flex-shrink-0"
                  title="Start guided tour"
                >
                  <HelpCircle className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground hover:text-foreground" />
                </Button>
              )}

              {showPlansButton && !showPlanStatus && (
                <div className="flex-shrink-0 xl:ml-[100px] ">
                  <PremiumButton onClick={() => {
                    interceptedNavigate(ACCOUNT_PLANS_URL);
                  }} />
                </div>
              )}
            </div>
          )}
        </div>

        <div className={cn(
          'flex items-center flex-shrink-0 min-w-0 ml-auto',
          isCard
            ? 'gap-1.5 lg:gap-2'
            : 'gap-1 sm:gap-1.5 md:gap-2 lg:gap-3 xl:gap-5 py-[5px] pl-3 lg:pl-6 xl:pl-10'
        )}>
          <div data-tour-slot="header-actions" className="contents" />

          {isRegularAdmin && showPlanStatus && !isCard && (
            <div className="hidden lg:flex items-center flex-shrink-0">
              <PlanCreditsBar />
            </div>
          )}

          {!isSuperAdmin && (
            <Button
              onClick={handleGenUIClick}
              title="NEXA AI (Beta)"
              className={cn(
                'group hidden sm:flex flex-shrink-0 items-center border-0 text-white shadow-sm transition-all hover:shadow-md hover:brightness-105 !bg-ai-gradient',
                isCard
                  ? '!h-9 gap-1.5 !rounded-[6px] px-2.5 lg:px-3'
                  : 'h-10 gap-2 !rounded-full px-4'
              )}
            >
              <span className="flex items-center gap-1.5">
                <span className="text-sm font-bold tracking-tight">NEXA AI</span>
                <span className="rounded-md border border-white/30 bg-white/15 px-1.5 py-0.5 text-[9px] font-normal uppercase leading-none tracking-wider text-white">
                  Beta
                </span>
              </span>
            </Button>
          )}

          {(isRegularAdmin || isAgent || isUser) && (
            <div className="flex-shrink-0">
              <NotificationDropdown
                triggerIcon={
                  <img
                    src={notification_icon}
                    alt="Notification"
                    className="w-4 h-4 sm:w-5 sm:h-5"
                  />
                }
                triggerClassName={isCard ? 'p-0 h-9 w-9 rounded-lg hover:bg-[#F2F4F7]' : undefined}
                badgeStyle={isCard ? 'dot' : 'count'}
              />
            </div>
          )}

          {!isSuperAdmin && !isAgency && (
            <SidebarAccountFooter compact={isCard} />
          )}

          {!showBusinessSwitcher && (
            <div className="hidden sm:block flex-shrink-0 min-w-0">
              {renderUserDropdown()}
            </div>
          )}
        </div>
      </header>

      {mobileSearchOpen && (
        <div
          className="sm:hidden fixed inset-0 z-[60] bg-black/40"
          onClick={() => {
            setMobileSearchOpen(false);
            setSearchQuery('');
          }}
        >
          <div
            className="bg-background border-b border-border shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-3 py-3">
              <div className="flex items-center flex-1 min-w-0 h-10 px-3 gap-2 rounded-md border border-slate-300 bg-white">
                <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <input
                  ref={mobileSearchInputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runPageSearch();
                    }
                    if (e.key === 'Escape') {
                      setMobileSearchOpen(false);
                      setSearchQuery('');
                    }
                  }}
                  placeholder="Search..."
                  className="bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground/50 text-foreground flex-1 min-w-0"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMobileSearchOpen(false);
                  setSearchQuery('');
                }}
                className="h-10 px-3 text-sm font-medium"
              >
                Cancel
              </Button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto scrollbar-thin px-3 pb-3">
              <div className="flex flex-col gap-1">
                {searchResults.length > 0 ? (
                  searchResults.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => {
                        openSearchResult(item, navigate);
                        setMobileSearchOpen(false);
                        setSearchQuery('');
                      }}
                      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/60 active:bg-muted cursor-pointer transition-colors"
                    >
                      <div
                        className="flex items-center justify-center p-1.5 rounded-md text-white flex-shrink-0"
                        style={{ background: 'var(--secondary-color)' }}
                      >
                        <item.icon className="h-4 w-4" />
                      </div>
                      <span className="text-sm font-medium text-foreground truncate">
                        {item.title}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="py-6 text-center">
                    <p className="text-sm text-muted-foreground">No matches found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!isSuperAdmin && !isNexaOpen && !isInboxConversationOpen && !dockedChatOpen && (
        <Button
          onClick={handleGenUIClick}
          title="NEXA AI (Beta)"
          aria-label="Open NEXA AI"
          data-nexa-fab-raised={raiseNexaFab ? 'true' : undefined}
          className={cn(
            'sm:hidden fixed right-5 z-[90] !h-[50px] !w-[50px] !min-h-0 !rounded-full border-0 !p-0 text-white shadow-lg transition-all hover:shadow-xl hover:brightness-105 !bg-ai-gradient',
            raiseNexaFab ? 'bottom-24' : 'bottom-5',
          )}
        >
          <img
            src={nexa_ai_icon}
            alt=""
            className="h-6 w-6 object-contain"
          />
        </Button>
      )}
    </>
  );
};
